import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { formatCurrencyAr, parseLocalizedNumber } from '../utils/numberFormat';
import {
  ArrowLeft,
  Landmark,
  Save,
  Check,
  RefreshCcw,
  CreditCard,
  DollarSign,
  Info,
  Wallet,
  Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';

function BancosForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [form, setForm] = useState({
    name: '',
    cbu_alias: '',
    currency: 'ARS',
    initial_balance: 0,
  });

  useEffect(() => {
    if (id) fetchBanco();
  }, [id]);

  async function fetchBanco() {
    setLoading(true);
    try {
      const { data } = await supabase.from('banks').select('*').eq('id', id).single();
      if (data) setForm(data);
    } catch (error) {
      toast.error('Error al cargar la cuenta');
    } finally {
      setLoading(false);
    }
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id || saving) return;

    setSaving(true);
    // name/cbu_alias pueden venir null desde la base (cbu_alias es nullable y
    // hay cuentas cargadas sin alias): sin el String() el toUpperCase revienta
    // al guardar la edicion.
    const payload = {
      ...form,
      name: String(form.name || '').toUpperCase(),
      cbu_alias: String(form.cbu_alias || '').toUpperCase() || null,
      initial_balance: parseLocalizedNumber(form.initial_balance) ?? 0,
      current_balance: id ? form.current_balance : (parseLocalizedNumber(form.initial_balance) ?? 0),
      organization_id: profile.organization_id,
    };

    try {
      const { error } = id
        ? await supabase.from('banks').update(payload).eq('id', id)
        : await supabase.from('banks').insert([payload]);

      if (error) throw error;

      setIsSuccess(true);
      toast.success('CUENTA SINCRONIZADA');

      setTimeout(() => {
        navigate('/sistema/bancos');
      }, 1500);
    } catch (e) {
      toast.error('Error al procesar la operación');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">
          Sincronizando activos...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-5 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/sistema/bancos')}
              title="Volver al listado de bancos"
              className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
              <Landmark size={18} className="text-[#3FF2AA]" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
                {id ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                Configuración operativa de tesorería
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div
            title="Estado actual del formulario"
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Modo</p>
              <Building2 size={16} className="text-[#51A7FD]" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight uppercase">
              {id ? 'Edicion' : 'Alta'}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Legajo bancario
            </p>
          </div>

          <div
            title="Moneda principal con la que va a operar la cuenta"
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Moneda</p>
              <DollarSign size={16} className="text-emerald-500" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight uppercase">
              {form.currency || 'ARS'}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Base operativa
            </p>
          </div>

          <div
            title="Saldo cargado al crear la cuenta bancaria"
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                Saldo inicial
              </p>
              <Wallet size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
              {formatCurrencyAr(parseLocalizedNumber(form.initial_balance) ?? 0)}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Carga de apertura
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <form onSubmit={handleSave} className="xl:col-span-8 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
                <Landmark size={16} className="text-[#3FF2AA]" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                  Datos de la cuenta
                </h3>
              </div>

              <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3">
                <div className="md:col-span-7">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    Nombre de la institución
                  </label>
                  <input
                    type="text"
                    required
                    data-onboarding-id="banco-name-input"
                    placeholder="EJ: BANCO GALICIA, MERCADO PAGO..."
                    title="Nombre visible de la cuenta o entidad"
                    className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD] text-slate-700 placeholder:text-slate-400"
                    value={form.name || ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div className="md:col-span-5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    Moneda base
                  </label>
                  <select
                    title="Seleccionar moneda principal de operación"
                    className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD] text-slate-700"
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  >
                    <option value="ARS">ARS - Pesos Argentinos</option>
                    <option value="USD">USD - Dolar Estadounidense</option>
                    <option value="USDT">USDT - Tether Digital</option>
                  </select>
                </div>

                <div className="md:col-span-8">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    CBU / Alias / CVU
                  </label>
                  <input
                    type="text"
                    placeholder="REFERENCIA DE CUENTA"
                    title="Alias, CBU o referencia operativa de la cuenta"
                    className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD] text-slate-700 placeholder:text-slate-400"
                    value={form.cbu_alias || ''}
                    onChange={(e) => setForm({ ...form, cbu_alias: e.target.value })}
                  />
                </div>

                <div className="md:col-span-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                    Saldo inicial
                  </label>
                  <LocalizedNumberInput
                    decimals={2}
                    disabled={id}
                    selectOnFocus
                    title={id ? 'El saldo inicial solo puede cargarse al crear la cuenta' : 'Saldo de apertura de la cuenta'}
                    className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black tracking-tight outline-none focus:border-[#51A7FD] disabled:opacity-60 disabled:bg-slate-100 text-slate-700"
                    value={form.initial_balance}
                    onValueChange={(display) => setForm({ ...form, initial_balance: display })}
                    placeholder="0,00"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
                <Info size={16} className="text-[#3FF2AA]" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                  Criterio operativo
                </h3>
              </div>
              <div className="p-4 sm:p-5">
                <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4 flex gap-3">
                  <Info size={18} className="text-[#51A7FD] shrink-0 mt-0.5" />
                  <p className="text-[10px] font-bold text-blue-700 uppercase leading-snug tracking-tight">
                    El saldo inicial solo puede definirse al crear la cuenta. Los ingresos, egresos y
                    transferencias posteriores van a ajustar el saldo actual automáticamente.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 pb-8">
              <button
                data-onboarding-id="banco-submit"
                type="submit"
                disabled={saving || isSuccess}
                title="Guardar configuración de la cuenta bancaria"
                className={`w-full h-12 rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] transition-all flex items-center justify-center gap-3 shadow-lg ${
                  isSuccess
                    ? 'bg-[#3ff2aa] text-[#0B2A36] shadow-emerald-200'
                    : 'bg-[#3ff2aa] text-[#0B2A36] hover:brightness-105 active:scale-[0.99] shadow-emerald-100 disabled:opacity-50 disabled:shadow-none'
                }`}
              >
                {isSuccess ? (
                  <>
                    <Check size={18} strokeWidth={3} className="animate-in zoom-in duration-300" /> Guardado
                  </>
                ) : saving ? (
                  <>
                    <RefreshCcw size={16} className="animate-spin" /> Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} /> Confirmar configuracion
                  </>
                )}
              </button>
            </div>
          </form>

          <aside className="xl:col-span-4 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
                <Wallet size={16} className="text-[#3FF2AA]" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                  Resumen rapido
                </h3>
              </div>
              <div className="p-4 sm:p-5 space-y-3">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cuenta</p>
                  <p className="mt-1 text-[13px] font-black text-slate-800 uppercase break-words">
                    {form.name || 'Sin nombre cargado'}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Referencia</p>
                  <p className="mt-1 text-[11px] font-black text-slate-800 uppercase break-all">
                    {form.cbu_alias || 'Sin alias o CBU'}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Base operativa
                  </p>
                  <p className="mt-1 text-[12px] font-black text-slate-800 uppercase">
                    {form.currency || 'ARS'}
                  </p>
                  <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Apertura: {formatCurrencyAr(parseLocalizedNumber(form.initial_balance) ?? 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
                <CreditCard size={16} className="text-[#3FF2AA]" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                  Guia de carga
                </h3>
              </div>
              <div className="p-4 sm:p-5 space-y-3">
                <p className="text-[11px] font-bold text-slate-600 leading-relaxed">
                  Usa un nombre claro para identificar la cuenta, carga el alias o CBU de referencia y
                  defini la moneda real con la que se va a operar.
                </p>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Tip</p>
                  <p className="mt-1 text-[11px] font-bold text-emerald-800 leading-relaxed">
                    Si la cuenta ya esta en uso, edita solo datos descriptivos. El saldo actual se ajusta con
                    movimientos, no tocando manualmente la apertura.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default BancosForm;
